import axios from 'axios';

/**
 * InPods AI Tools Service
 *
 * API client for the IMSCC Extractor, Lecture Slide Tool, and AQMS Audit REST endpoints.
 * Each tool is callable via named REST endpoint (same underlying code as MCP tools).
 */

// All tools are now served from the Django backend on port 8000
const DJANGO_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Helper to get JWT token from localStorage (same as api/config.js)
const getAuthHeader = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const imscc = axios.create({ baseURL: DJANGO_BASE, headers: { 'Content-Type': 'application/json' } });
const lst = axios.create({ baseURL: DJANGO_BASE, headers: { 'Content-Type': 'application/json' } });
const aqms = axios.create({ baseURL: DJANGO_BASE, headers: { 'Content-Type': 'application/json' } });

// Add JWT auth to all tool requests
[imscc, lst, aqms].forEach(instance => {
  instance.interceptors.request.use(config => {
    Object.assign(config.headers, getAuthHeader());
    return config;
  });
});

class ToolsService {

  // ============ IMSCC Extractor (port 8001) ============

  /** Upload an .imscc file to the IMSCC extractor server */
  async uploadImscc(file) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await imscc.post('/api/imscc/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  }

  /** Parse IMSCC structure (modules, items, resources) */
  async parseImscc(filename) {
    const res = await imscc.post('/api/imscc/parse', { filename });
    return res.data;
  }

  /** Full extraction pipeline (parse + burst + content + assessments + syllabus) */
  async extractCanvas(filename) {
    const res = await imscc.post('/api/imscc/extract-canvas', { filename });
    return res.data;
  }

  /** Extract all questions as JSON + CSV */
  async extractAssessments(filename) {
    const res = await imscc.post('/api/imscc/assessments', { filename });
    return res.data;
  }

  /** Detect and extract syllabus with COs */
  async extractSyllabus(filename) {
    const res = await imscc.post('/api/imscc/syllabus', { filename });
    return res.data;
  }

  /** Export syllabus as CSV reference file */
  async exportSyllabusCsv(filename) {
    const res = await imscc.post('/api/imscc/export-syllabus-csv', { filename });
    return res.data;
  }

  /** Export all as ZIP bundle */
  async exportBundle(filename) {
    const res = await imscc.post('/api/imscc/export-bundle', { filename });
    return res.data;
  }

  /** List uploaded files on IMSCC server */
  async listImsccUploads() {
    const res = await imscc.get('/api/imscc/uploads');
    return res.data;
  }

  // ============ Lecture Slide Tool (Django /api/lst/) ============

  /** Upload a PDF/PPTX to the LST server */
  async uploadSlides(file) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await lst.post('/api/lst/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  }

  /** Extract slides from PDF/PPTX */
  async extractSlides(filename, dpi = 200) {
    const res = await lst.post('/api/lst/extract-slides', { filename, dpi });
    return res.data;
  }

  /** AI-expand slides into detailed study notes */
  async expandNotes(slidesJson, detailLevel = 'standard') {
    const res = await lst.post('/api/lst/expand-notes', { slides_json: slidesJson, detail_level: detailLevel });
    return res.data;
  }

  /** AI-assisted editing with natural language instructions */
  async editSlides(expandedJson, instructions, slideNumbers = '') {
    const res = await lst.post('/api/lst/edit-slides', {
      expanded_json: expandedJson,
      instructions,
      slide_numbers: slideNumbers
    });
    return res.data;
  }

  /** Generate professor-style narration scripts */
  async generateNarration(expandedJson, skipSlides = '') {
    const res = await lst.post('/api/lst/narrate', {
      expanded_json: expandedJson,
      skip_slides: skipSlides
    });
    return res.data;
  }

  /** Generate flowing lecture with transitions */
  async generateLectureFlow(narrationJson) {
    const res = await lst.post('/api/lst/lecture-flow', { narration_json: narrationJson });
    return res.data;
  }

  /** Generate TTS audio (MP3 per slide) */
  async generateTts(lectureFlowJson, voice = 'en-US-AndrewMultilingualNeural') {
    const res = await lst.post('/api/lst/tts', { lecture_flow_json: lectureFlowJson, voice });
    return res.data;
  }

  /** Generate editable HTML review page */
  async generateReview(expandedJson, narrationJson = '', courseName = '', lessonName = '') {
    const res = await lst.post('/api/lst/generate-review', {
      expanded_json: expandedJson,
      narration_json: narrationJson,
      course_name: courseName,
      lesson_name: lessonName
    });
    return res.data;
  }

  /** Apply review feedback to update JSON */
  async incorporateReview(reviewedHtml, expandedJson, narrationJson = '') {
    const res = await lst.post('/api/lst/incorporate-review', {
      reviewed_html: reviewedHtml,
      expanded_json: expandedJson,
      narration_json: narrationJson
    });
    return res.data;
  }

  /** Generate questions review HTML */
  async generateQuestionsReview(csvPath, courseName = '') {
    const res = await lst.post('/api/lst/generate-questions-review', {
      csv_path: csvPath,
      course_name: courseName
    });
    return res.data;
  }

  /** Export PDF (study guide, slides only, or lecture script) */
  async exportPdf(jsonFilename, exportType = 'study_guide') {
    const res = await lst.post('/api/lst/export', { json_filename: jsonFilename, export_type: exportType });
    return res.data;
  }

  /** List uploaded files on LST server */
  async listLstUploads() {
    const res = await lst.get('/api/lst/uploads');
    return res.data;
  }

  // ============ AQMS Audit ============

  /** Run full AQMS quality audit report */
  async runAudit(questionsFile, syllabusFile) {
    const formData = new FormData();
    formData.append('questions', questionsFile);
    formData.append('syllabus', syllabusFile);
    const res = await aqms.post('/api/generate-aqms-report', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  }

  /** Convert syllabus PDF/CSV to markdown */
  async convertSyllabus(file) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await aqms.post('/api/convert-syllabus', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  }
}

const toolsService = new ToolsService();
export default toolsService;
